import React, { useState, useRef, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import ReactCrop, { convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import classNames from 'classnames';
import { ensureCurrentUser } from '../../util/data';
import { isUploadImageOverLimitError } from '../../util/errors';
import { updateIntercomUser } from '../../util/intercom';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  NamedRedirect,
  Form,
  FieldTextInput,
  LocationAutocompleteInputField,
  FieldSelectModern,
  Modal,
  Button,
  Avatar,
  ImageFromFile,
  IconSpinner,
  TopbarBlank,
  OnboardingAccountMenu,
} from '../../components';
import { TopbarContainer } from '../../containers';
import config from '../../config';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { completeBasicInfo } from '../../util/api';
import { uploadImage, updateProfile, loadData } from './BasicInformationPage.duck';

import css from './BasicInformationPage.css';

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000;

// --- Image crop helpers (from ProfileSettingsForm) ---

function canvasPreview(image, canvas, crop) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio || 1;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );
}

function getCenteredAspectCrop(mediaWidth, mediaHeight, aspect) {
  let cropWidth, cropHeight;
  if (mediaWidth / mediaHeight > aspect) {
    cropHeight = mediaHeight;
    cropWidth = cropHeight * aspect;
  } else {
    cropWidth = mediaWidth;
    cropHeight = cropWidth / aspect;
  }
  const x = ((mediaWidth - cropWidth) / 2 / mediaWidth) * 100;
  const y = ((mediaHeight - cropHeight) / 2 / mediaHeight) * 100;
  const width = (cropWidth / mediaWidth) * 100;
  const height = (cropHeight / mediaHeight) * 100;
  return { unit: '%', x, y, width, height, aspect };
}

function CropImage({
  src,
  crop,
  setCrop,
  completedCrop,
  setCompletedCrop,
  imgRef,
  previewCanvasRef,
}) {
  const aspect = 1;

  function handleImageLoad(e) {
    const { width, height } = e.currentTarget;
    const centeredCrop = getCenteredAspectCrop(width, height, aspect);
    setCrop(centeredCrop);
    setCompletedCrop(convertToPixelCrop(centeredCrop, width, height));
  }

  useEffect(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
    }
  }, [completedCrop, imgRef, previewCanvasRef]);

  return (
    <div>
      <ReactCrop
        crop={crop}
        aspect={aspect}
        onChange={(_, percentCrop) => setCrop(percentCrop)}
        onComplete={c => setCompletedCrop(c)}
        keepSelection
        circularCrop
      >
        <img
          ref={imgRef}
          src={src}
          alt="Crop preview"
          onLoad={handleImageLoad}
          style={{ maxWidth: '100%', maxHeight: '65vh' }}
        />
      </ReactCrop>
      <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
    </div>
  );
}

// --- Camera icon SVG (outline style) ---
const CameraIcon = () => (
  <svg
    className={css.cameraIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path
      fill="none"
      d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
    />
    <circle fill="none" cx="12" cy="13" r="4" />
  </svg>
);

// --- Check icon (BiCheck style) for image upload hints ---
const CheckIcon = () => (
  <svg className={css.checkIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

// --- Close icon for language chips ---
const CloseIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 1l10 10M11 1L1 11" />
  </svg>
);

// --- Validators ---

const requiredField = message => value => {
  return value ? undefined : message;
};

const locationSelectedValidator = message => value => {
  return value && value.selectedPlace ? undefined : message;
};

const identity = v => v;

// --- Main component ---

const BasicInformationPageComponent = props => {
  const {
    currentUser,
    image,
    uploadImageError,
    uploadInProgress,
    updateInProgress,
    updateProfileError,
    scrollingDisabled,
    onImageUpload,
    onUpdateProfile,
    onFetchCurrentUser,
    onManageDisableScrolling,
    intl,
    history,
  } = props;

  // All hooks must be called before any early returns
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [uploadDelay, setUploadDelay] = useState(false);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const imageReaderRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadDelayTimeoutRef = useRef(null);
  const prevUploadInProgressRef = useRef(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [languagesTouched, setLanguagesTouched] = useState(false);

  useEffect(() => {
    if (prevUploadInProgressRef.current && !uploadInProgress) {
      setUploadDelay(true);
      uploadDelayTimeoutRef.current = setTimeout(() => setUploadDelay(false), UPLOAD_CHANGE_DELAY);
    }
    prevUploadInProgressRef.current = uploadInProgress;
  }, [uploadInProgress]);

  useEffect(() => {
    imageReaderRef.current = new FileReader();
    imageReaderRef.current.addEventListener('load', () => {
      setCropImageSrc(imageReaderRef.current.result);
    });
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true });
    }
  }, []);

  // Push basicInfoDone to Intercom whenever it changes
  const basicInfoDone = !!currentUser?.attributes?.profile?.metadata?.basicInfoDone;
  const prevBasicInfoDoneRef = useRef(basicInfoDone);
  useEffect(() => {
    if (prevBasicInfoDoneRef.current !== basicInfoDone && typeof window !== 'undefined') {
      updateIntercomUser({ basicInfoDone });
    }
    prevBasicInfoDoneRef.current = basicInfoDone;
  }, [basicInfoDone]);

  const user = ensureCurrentUser(currentUser);
  const metadata = user.attributes?.profile?.metadata || {};

  // Redirect if basicInfoDone
  if (user.id && metadata.basicInfoDone) {
    return <NamedRedirect name="FreelancerOnboardingPage" />;
  }

  // Language options from config
  const languageOptions = config.custom.filters
    .find(f => f.id === 'languages')
    .config.options.map(lang => ({
      ...lang,
      value: lang.key,
      label: intl.formatMessage({ id: lang.label.props.id }),
    }));

  const availableLanguages = languageOptions.filter(
    opt => !selectedLanguages.some(sel => sel.key === opt.key)
  );

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      imageReaderRef.current.readAsDataURL(file);
      setIsSelectionModalOpen(true);
    }
  };

  const handleCropConfirm = () => {
    const imageEl = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!imageEl || !previewCanvas || !completedCrop) return;

    setIsSelectionModalOpen(false);

    previewCanvas.toBlob(
      blob => {
        if (blob) {
          const tempId = `avatar_${Date.now()}`;
          blob.name = tempId;
          onImageUpload({ id: tempId, file: blob });
        }
      },
      'image/jpeg',
      1
    );
  };

  const handleLanguageSelect = option => {
    setLanguagesTouched(true);
    if (option && !selectedLanguages.some(l => l.key === option.key)) {
      setSelectedLanguages([...selectedLanguages, option]);
    }
  };

  const handleLanguageRemove = key => {
    setLanguagesTouched(true);
    setSelectedLanguages(selectedLanguages.filter(l => l.key !== key));
  };

  const handleSubmit = values => {
    const locationData = values.location || {};
    const selectedPlace = locationData.selectedPlace || {};
    const languageKeys = selectedLanguages.map(l => l.key);
    const existingPrivateData = user.attributes?.profile?.privateData || {};

    const profile = {
      publicData: {
        whoAmI: values.whoAmI,
        location: locationData,
        locationCity: selectedPlace.address ? selectedPlace.address.split(',')[0]?.trim() : '',
        locationCountry: selectedPlace.country || '',
        locationCountryCode: selectedPlace.countryCode || '',
        languages: languageKeys,
      },
      privateData: {
        ...existingPrivateData,
        notificationLanguage: languageKeys.includes('finnish') ? 'finnish' : 'english',
      },
    };

    if (image && image.imageId) {
      profile.profileImageId = image.imageId;
    }

    const userId = user.id?.uuid;
    onUpdateProfile(profile)
      .then(() => completeBasicInfo({ userId }))
      .then(() => onFetchCurrentUser());
  };

  // Profile image display logic
  const profileImageId = user.profileImage ? user.profileImage.id : null;
  const profileImage = image || { imageId: profileImageId };
  const fileExists = !!profileImage.file;
  const fileUploadInProgress = uploadInProgress && fileExists;
  const delayAfterUpload = profileImage.imageId && uploadDelay;
  const hasImage = profileImage.imageId || fileUploadInProgress;

  const uploadingOverlay =
    uploadInProgress || uploadDelay ? (
      <div className={css.uploadingImageOverlay}>
        <IconSpinner />
      </div>
    ) : null;

  const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
  const transientUser = { ...user, profileImage: transientUserProfileImage };

  const imageFromFile =
    fileExists && (fileUploadInProgress || delayAfterUpload) ? (
      <ImageFromFile
        id={profileImage.id}
        rootClassName={css.uploadingImage}
        aspectRatioClassName={css.squareAspectRatio}
        file={profileImage.file}
      >
        {uploadingOverlay}
      </ImageFromFile>
    ) : null;

  const avatarClasses = classNames(css.avatar, {
    [css.avatarInvisible]: uploadDelay,
  });

  const avatarComponent =
    !fileUploadInProgress && profileImage.imageId ? (
      <Avatar
        rootClassName={avatarClasses}
        renderSizes="96px"
        user={transientUser}
        disableProfileLink
        disableVerifiedBadge
      />
    ) : null;

  const title = intl.formatMessage({ id: 'BasicInformationPage.title' });

  // Upload error display
  let imageError = null;
  if (isUploadImageOverLimitError(uploadImageError)) {
    imageError = (
      <p className={css.imageUploadError}>
        <FormattedMessage id="BasicInformationPage.imageUploadFileTooLarge" />
      </p>
    );
  } else if (uploadImageError) {
    imageError = (
      <p className={css.imageUploadError}>
        <FormattedMessage id="BasicInformationPage.imageUploadFailed" />
      </p>
    );
  }

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarBlank>
            <OnboardingAccountMenu />
          </TopbarBlank>
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          <div className={css.root}>
            <div className={css.card}>
              <div className={css.heading}>
                <h1 className={css.title}>
                  <FormattedMessage id="BasicInformationPage.heading" />
                </h1>
              </div>

              <FinalForm
                onSubmit={handleSubmit}
                render={({ handleSubmit: formHandleSubmit, invalid, form, values }) => {
                  const languagesValid = selectedLanguages.length > 0;
                  const submitDisabled =
                    invalid || !languagesValid || uploadInProgress || updateInProgress;

                  return (
                    <Form onSubmit={formHandleSubmit}>
                      <div className={css.fieldGroup}>
                        {/* Profile image */}
                        <div className={css.profileImageSection}>
                          <label
                            htmlFor="basicInfoProfileImage"
                            style={{ cursor: 'pointer', margin: 0 }}
                          >
                            {hasImage ? (
                              <div className={css.avatarContainer}>
                                {imageFromFile}
                                {avatarComponent}
                              </div>
                            ) : (
                              <div className={css.profileImageUpload}>
                                <CameraIcon />
                              </div>
                            )}
                          </label>
                          <input
                            ref={fileInputRef}
                            accept={ACCEPT_IMAGES}
                            id="basicInfoProfileImage"
                            className={css.uploadAvatarInput}
                            type="file"
                            onChange={handleFileChange}
                            onClick={e => {
                              e.target.value = null;
                            }}
                          />
                          <div className={css.profileImageInfo}>
                            <span className={css.profileImageLabel}>
                              <FormattedMessage id="BasicInformationPage.profileImageLabel" />{' '}
                              <span className={css.profileImageOptional}>
                                <FormattedMessage id="BasicInformationPage.profileImageOptionalText" />
                              </span>
                            </span>
                            <span className={css.profileImageHintRow}>
                              <CheckIcon />
                              <span className={css.profileImageUploadText}>
                                <FormattedMessage id="BasicInformationPage.profileImageUploadText" />
                              </span>
                            </span>
                            <span className={css.profileImageHintRow}>
                              <CheckIcon />
                              <span className={css.profileImageUploadText}>
                                <FormattedMessage id="BasicInformationPage.profileImageUploadHint" />
                              </span>
                            </span>
                          </div>
                          {imageError}
                        </div>

                        {/* Profile title */}
                        <FieldTextInput
                          id="whoAmI"
                          name="whoAmI"
                          maxLength="50"
                          type="text"
                          showSpacer={false}
                          label={intl.formatMessage({
                            id: 'BasicInformationPage.profileTitleLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id: 'BasicInformationPage.profileTitlePlaceholder',
                          })}
                          validate={requiredField(
                            intl.formatMessage({
                              id: 'BasicInformationPage.profileTitleRequired',
                            })
                          )}
                        />

                        {/* Location */}
                        <LocationAutocompleteInputField
                          name="location"
                          labelClassName={css.fieldLabel}
                          label={intl.formatMessage({
                            id: 'BasicInformationPage.locationLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id: 'BasicInformationPage.locationPlaceholder',
                          })}
                          useDefaultPredictions={false}
                          format={identity}
                          valueFromForm={values.location}
                          validate={locationSelectedValidator(
                            intl.formatMessage({
                              id: 'BasicInformationPage.locationRequired',
                            })
                          )}
                          predictionType="place"
                        />

                        {/* Languages */}
                        <div>
                          <FieldSelectModern
                            id="languageSelect"
                            name="languageSelect"
                            useSearchIcon
                            label={intl.formatMessage({
                              id: 'BasicInformationPage.languagesLabel',
                            })}
                            placeholder={intl.formatMessage({
                              id: 'BasicInformationPage.languagesPlaceholder',
                            })}
                            options={availableLanguages}
                            value={null}
                            onChange={option => {
                              handleLanguageSelect(option);
                              // Reset the select field after selection
                              setTimeout(() => form.change('languageSelect', null), 0);
                            }}
                          />
                          {selectedLanguages.length > 0 && (
                            <div className={css.languageChips}>
                              {selectedLanguages.map(lang => (
                                <div key={lang.key} className={css.languageChip}>
                                  <span>{lang.label}</span>
                                  <span
                                    className={css.languageChipRemove}
                                    onClick={() => handleLanguageRemove(lang.key)}
                                  >
                                    <CloseIcon />
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {languagesTouched && selectedLanguages.length === 0 && (
                            <p className={css.languageError}>
                              <FormattedMessage id="BasicInformationPage.languagesRequired" />
                            </p>
                          )}
                        </div>
                      </div>

                      {updateProfileError && (
                        <p className={css.error}>
                          <FormattedMessage id="BasicInformationPage.updateProfileFailed" />
                        </p>
                      )}

                      <div className={css.buttonRow}>
                        <Button
                          rootClassName={css.nextButton}
                          type="submit"
                          inProgress={updateInProgress}
                          disabled={submitDisabled}
                          onClick={() => setLanguagesTouched(true)}
                        >
                          <FormattedMessage id="BasicInformationPage.nextButton" />
                        </Button>
                      </div>
                    </Form>
                  );
                }}
              />

              {/* Crop modal */}
              <Modal
                id="BasicInformationPage.cropModal"
                contentClassName={css.selectImageAreaModalContent}
                isOpen={isSelectionModalOpen}
                onClose={() => setIsSelectionModalOpen(false)}
                usePortal
                onManageDisableScrolling={onManageDisableScrolling}
              >
                <div className={css.cropImageWrapper}>
                  <CropImage
                    src={cropImageSrc}
                    crop={crop}
                    setCrop={setCrop}
                    completedCrop={completedCrop}
                    setCompletedCrop={setCompletedCrop}
                    imgRef={imgRef}
                    previewCanvasRef={previewCanvasRef}
                  />
                </div>
                <Button className={css.submitImageButton} type="button" onClick={handleCropConfirm}>
                  <FormattedMessage id="BasicInformationPage.cropModalSave" />
                </Button>
              </Modal>
            </div>
          </div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    image,
    uploadImageError,
    uploadInProgress,
    updateInProgress,
    updateProfileError,
  } = state.BasicInformationPage;
  return {
    currentUser,
    image,
    uploadImageError,
    uploadInProgress,
    updateInProgress,
    updateProfileError,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onImageUpload: data => dispatch(uploadImage(data)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onFetchCurrentUser: () => dispatch(fetchCurrentUser()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const BasicInformationPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(BasicInformationPageComponent);

BasicInformationPage.loadData = loadData;

export default BasicInformationPage;
